import React from "react";
import { Box, Typography, Divider, Grid, Button, Sheet, Chip, Stack } from "@mui/joy";
import { FileDown, Send, CheckCircle2, User, Building2 } from "lucide-react";
import SideModal from "../SideModal";

interface IContractPreview {
    open: boolean;
    onClose: () => void;
    data: any;
    onGenerate?: (overrides: any) => void;
    isGenerating?: boolean;
}

export default function ContractPreview({ open, onClose, data, onGenerate, isGenerating }: IContractPreview) {
    const [localData, setLocalData] = React.useState(data);

    React.useEffect(() => {
        setLocalData(data);
    }, [data]);

    const COLOR_HEADER = "#1D3557";
    const COLOR_SUB = "#E6F0FA";
    const COLOR_INSURANCE = "#FFFDEC";

    const GridCell = ({ label, value, flex = 1 }: { label: string; value: string; flex?: number | string }) => (
        <Box sx={{ flex, p: 1, border: "0.2px solid", borderColor: "neutral.200", bgcolor: "white", minWidth: 0 }}>
            <Typography
                sx={{ fontSize: "8px", fontWeight: 700, color: "neutral.500", textTransform: "uppercase", mb: 0.2 }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "neutral.800",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {value || "—"}
            </Typography>
        </Box>
    );

    const SectionHeader = ({ num, title }: { num: string; title: string }) => (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: COLOR_HEADER,
                px: 1.5,
                py: 0.5,
                mt: 2,
            }}
        >
            <Typography sx={{ fontWeight: 800, fontSize: "12px", mr: 1.5, opacity: 0.8, color: "white" }}>
                {num}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "11px", letterSpacing: "0.05em", color: "white" }}>
                {title}
            </Typography>
        </Box>
    );

    return (
        <SideModal
            showAdditionalHeadersIcons={false}
            open={open}
            onClose={onClose}
            portalBoxProps={{ width: 850 }}
            title="Document Preview"
            description="Service agreement preview."
        >
            <Box sx={{ bgcolor: "background.level1", minHeight: "100%", display: "flex", flexDirection: "column" }}>
                {/* PDF Content Wrapper */}
                <Box sx={{ p: 0, my: 3, flex: 1, display: "flex", justifyContent: "center", mb: 3 }}>
                    <Sheet
                        sx={{
                            width: "100%",
                            maxWidth: "800px",
                            bgcolor: "white",
                            p: 5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0,
                            fontFamily: "'Albert Sans', sans-serif",
                            border: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        {/* Header */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: COLOR_HEADER,
                                        borderRadius: "xs",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Building2 color="white" size={20} />
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{ color: COLOR_HEADER, fontWeight: 900, fontSize: "14px", lineHeight: 1 }}
                                    >
                                        1ST COLORADO
                                    </Typography>
                                    <Typography sx={{ color: COLOR_HEADER, fontWeight: 500, fontSize: "12px" }}>
                                        ROOFING
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ textAlign: "right" }}>
                                <Typography sx={{ fontWeight: 900, fontSize: "18px", color: COLOR_HEADER }}>
                                    SERVICE AGREEMENT
                                </Typography>
                                <Typography sx={{ fontSize: "10px", color: "neutral.500", fontWeight: 600 }}>
                                    REF: {localData?.Header?.ReferenceNumber} | {localData?.OwnerInfo?.Date}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Section 1: OWNER & PROJECT INFORMATION */}
                        <SectionHeader num="1" title="OWNER & PROJECT INFORMATION" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="OWNER NAME" value={localData?.OwnerInfo?.Name} />
                            <GridCell label="SALES REPRESENTATIVE" value={localData?.OwnerInfo?.SalesRep} />
                            <GridCell label="CLAIM NUMBER" value={localData?.OwnerInfo?.ClaimNumber || "N/A"} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="PROJECT ADDRESS" value={localData?.OwnerInfo?.Address} flex={2} />
                            <GridCell
                                label="INSURANCE COMPANY"
                                value={localData?.OwnerInfo?.InsuranceCompany || "N/A"}
                            />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="PHONE" value={localData?.OwnerInfo?.Phone || "N/A"} />
                            <GridCell label="EMAIL" value={localData?.OwnerInfo?.Email || "N/A"} />
                            <GridCell
                                label="CITY / STATE / ZIP"
                                value={`${localData?.OwnerInfo?.City || "N/A"}, ${localData?.OwnerInfo?.State || ""} ${localData?.OwnerInfo?.Zip || ""}`}
                                flex={1.5}
                            />
                        </Stack>

                        {/* Section 2: ROOFING SCOPE OF WORK */}
                        <SectionHeader num="2" title="ROOFING SCOPE OF WORK" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="ROOF MATERIAL" value={localData?.RoofingScope?.Material} />
                            <GridCell label="ROOF COLOR" value={localData?.RoofingScope?.Color} />
                            <GridCell label="ROOF PITCH" value={localData?.RoofingScope?.Pitch} />
                            <GridCell label="TEAR OFF LAYERS" value={localData?.RoofingScope?.Layers || "1"} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="UNDERLAYMENT" value="Synthetic 30lb" />
                            <GridCell label="ICE & WATER SHIELD" value="Eaves & Valleys" />
                            <GridCell label="STARTER STRIP" value="ALL Eaves & Rakes" />
                            <GridCell label="WARRANTY" value="30 Years" />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="EAVE DRIP EDGE" value={localData?.RoofingScope?.EaveDripEdge || "N/A"} />
                            <GridCell label="RAKE DRIP EDGE" value={localData?.RoofingScope?.RakeDripEdge || "N/A"} />
                            <GridCell label="VALLEY TYPE" value={localData?.RoofingScope?.ValleyType || "N/A"} />
                            <GridCell label="HIP & RIDGE CAP" value="Factory Match" />
                        </Stack>

                        {/* Section 3: NAILING & VENTILATION */}
                        <SectionHeader num="3" title="NAILING & VENTILATION" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="FIELD NAIL SIZE" value={'1 1/4"'} />
                            <GridCell label="NAILS PER SHINGLE" value="6 Nails" />
                            <GridCell label="RIDGE NAIL SIZE" value={'1 1/2"'} />
                            <GridCell label="NAILS PER CAP" value="4 Nails" />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="ROOF VENTILATION" value="Ridge Vent — Painted" flex={1.5} />
                            <GridCell label="PLUMBING VENTS" value="Yes — Painted" />
                            <GridCell label="FURNACE VENT" value="Jack/Cap/Collar" />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="REFLASH CHIMNEY" value="Yes" />
                            <GridCell label="REFLASH SKYLIGHTS" value="Yes" />
                            <GridCell label="REPLACE SKYLIGHTS" value="As Needed" />
                        </Stack>

                        {/* Section 4: SPECIAL PROJECT NOTES */}
                        <SectionHeader num="4" title="SPECIAL PROJECT NOTES" />
                        <Box sx={{ p: 1.5, border: "0.2px solid", borderColor: "neutral.200", minHeight: 60 }}>
                            <Typography sx={{ fontSize: "10px", fontStyle: "italic", color: "neutral.600" }}>
                                {localData?.Notes || "No special notes for this project."}
                            </Typography>
                        </Box>

                        {/* Section 5: GUTTER SCOPE OF WORK */}
                        <SectionHeader num="5" title="GUTTER SCOPE OF WORK" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="INSTALL NEW GUTTERS" value="Yes" />
                            <GridCell label="GUTTER SIZE" value={'6" Seamless'} />
                            <GridCell label="GUTTER COLOR" value="White" />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="INSTALL DOWNSPOUTS" value="Yes" />
                            <GridCell label="DOWNSPOUT SIZE" value={'3x4"'} />
                            <GridCell label="DOWNSPOUT COLOR" value="White" />
                        </Stack>

                        {/* Section 6: PRICING & PAYMENT SCHEDULE */}
                        <SectionHeader num="6" title="PRICING & PAYMENT SCHEDULE" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="ROOFING PRICE" value={localData?.Pricing?.RoofPrice || "$0.00"} />
                            <GridCell label="GUTTER PRICE" value={localData?.Pricing?.GutterPrice || "$0.00"} />
                            <GridCell
                                label="TOTAL CONTRACT PRICE"
                                value={localData?.Pricing?.TotalPrice || "$0.00"}
                                flex={1.5}
                            />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell
                                label="ESTIMATED START DATE"
                                value={localData?.Pricing?.EstimatedStartDate || "TBD"}
                            />
                            <GridCell label="PAYMENT SCHEDULE" value="50% at start; balance upon completion" flex={2} />
                        </Stack>

                        {/* Section 8: INSURANCE CLAIMS (The Yellow Box) */}
                        <SectionHeader num="8" title="SPECIAL CONDITIONS FOR INSURANCE CLAIMS" />
                        <Box sx={{ p: 1.5, bgcolor: COLOR_INSURANCE, border: "0.5px solid #E6D5A1" }}>
                            <Typography sx={{ fontSize: "9px", lineHeight: 1.4, color: "neutral.800" }}>
                                This agreement does not obligate the homeowners or 1st Colorado Roofing in any way
                                unless payment for damage is approved by both the homeowner’s insurance company as well
                                as 1st Colorado Roofing.
                            </Typography>
                        </Box>

                        {/* Section 9: SIGNATURES */}
                        <SectionHeader num="9" title="ACCEPTANCE & SIGNATURES" />
                        <Stack direction="row" sx={{ width: "100%", mt: 1, gap: 4 }}>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ borderBottom: "1px solid", borderColor: "neutral.400", mt: 3, mb: 1 }} />
                                <Typography sx={{ fontSize: "8px", fontWeight: 700 }}>OWNER SIGNATURE</Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ borderBottom: "1px solid", borderColor: "neutral.400", mt: 3, mb: 1 }} />
                                <Typography sx={{ fontSize: "8px", fontWeight: 700 }}>
                                    1ST COLORADO ROOFING REPRESENTATIVE
                                </Typography>
                            </Box>
                        </Stack>

                        {/* Bottom Branding */}
                        <Box
                            sx={{
                                mt: 4,
                                textAlign: "center",
                                borderTop: "2px solid",
                                borderColor: COLOR_HEADER,
                                pt: 1.5,
                            }}
                        >
                            <Typography
                                sx={{ fontSize: "9px", fontWeight: 800, color: COLOR_HEADER, letterSpacing: "2px" }}
                            >
                                WWW.1STCOLORADOROOFING.COM
                            </Typography>
                        </Box>
                    </Sheet>
                </Box>

                {/* Sticky Action Bar */}
                <Box
                    sx={{
                        p: 3,
                        pt: 2,
                        display: "flex",
                        gap: 2,
                        justifyContent: "flex-end",
                        mt: "auto",
                        position: "sticky",
                        bottom: 0,
                        bgcolor: "background.level1",
                        borderTop: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Button
                        variant="solid"
                        color="primary"
                        startDecorator={<FileDown size={20} />}
                        loading={isGenerating}
                        onClick={() => onGenerate?.(localData)}
                        sx={{ px: 4 }}
                    >
                        Generate Contract
                    </Button>
                    <Button variant="outlined" color="success" startDecorator={<Send size={20} />} sx={{ px: 4 }}>
                        Send to BoldSign
                    </Button>
                </Box>
            </Box>
        </SideModal>
    );
}
